import React from "react";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import FormField from "@cloudscape-design/components/form-field";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Pagination from "@cloudscape-design/components/pagination";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Table from "@cloudscape-design/components/table";

const TYPE_OPTIONS = [
	{ value: "", label: "All types" },
	{ value: "confirm", label: "Confirm" },
	{ value: "recovery", label: "Recovery" },
];

const STATUS_OPTIONS = [
	{ value: "", label: "All statuses" },
	{ value: "0", label: "0 — Pending" },
	{ value: "1", label: "1 — Processed / active" },
	{ value: "2", label: "2 — Attempts exhausted" },
	{ value: "5", label: "5 — Confirmed / used" },
	{ value: "6", label: "6 — Failed" },
	{ value: "7", label: "7 — Verification in progress" },
	{ value: "8", label: "8 — Suppressed / inactive" },
	{ value: "9", label: "9 — WAF blocked" },
	{ value: "10", label: "10 — Fallback in progress" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100].map((value) => ({ value, label: String(value) }));

const DATE_RANGE_I18N = {
	todayAriaLabel: "Today",
	nextMonthAriaLabel: "Next month",
	previousMonthAriaLabel: "Previous month",
	customRelativeRangeDurationLabel: "Duration",
	customRelativeRangeDurationPlaceholder: "Enter duration",
	customRelativeRangeOptionLabel: "Custom range",
	customRelativeRangeOptionDescription: "Define a custom range",
	customRelativeRangeUnitLabel: "Unit of time",
	formatRelativeRange: ({ startDate, endDate }) => `${startDate} - ${endDate}`,
	formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
	dateTimeConstraintText: "Date must be within range.",
	renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
	labelRelativeRange: "Relative range",
	labelAbsoluteRange: "Absolute range",
	dateTimePickerAriaLabel: "Date picker",
	dateTimePickerLabel: "Date",
	relativeModeTitle: "Relative mode",
	absoluteModeTitle: "Absolute mode",
	cancelButtonLabel: "Cancel",
	applyButtonLabel: "Apply",
	clearButtonAriaLabel: "Clear date range",
	clearButtonLabel: "Clear date range",
};

const pad = (value) => String(value).padStart(2, "0");
const formatDateOnly = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const createDefaultLogDateRange = (now = new Date()) => {
	const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const start = new Date(end);
	start.setDate(start.getDate() - 29);
	return { type: "absolute", startDate: formatDateOnly(start), endDate: formatDateOnly(end) };
};

export const smsStatusText = (status) => {
	const labels = {
		0: "Pending",
		1: "Processed / active",
		2: "Attempts exhausted",
		5: "Confirmed / used",
		6: "Failed",
		7: "Verification in progress",
		8: "Suppressed / inactive",
		9: "WAF blocked",
		10: "Fallback in progress",
	};
	return labels[Number(status)] || `Unknown (${status})`;
};

export const smsStatusLabel = (status) => {
	const text = smsStatusText(status);
	return text.startsWith("Unknown (") ? text : `${text} (${status})`;
};

const smsStatusType = (status) => {
	switch (Number(status)) {
		case 1:
		case 5:
			return "success";
		case 0:
		case 7:
		case 10:
			return "in-progress";
		case 2:
		case 6:
		case 9:
			return "error";
		case 8:
			return "stopped";
		default:
			return "info";
	}
};

const routingStatusLabel = (status) => {
	if (status === "accepted") return "Accepted by provider API";
	return String(status || "Unknown")
		.split("_")
		.map((part) => part ? part[0].toUpperCase() + part.slice(1) : part)
		.join(" ");
};

const routingStatusType = (status) => {
	if (status === "accepted") return "success";
	if (status === "sending") return "in-progress";
	if (status === "failed") return "error";
	if (status === "switched") return "warning";
	if (["suppressed", "skipped_condition", "skipped_limit"].includes(status)) return "stopped";
	return "info";
};

const formatDateTime = (value) => {
	if (!value) return "—";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? String(value) : `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

const getErrorText = (error) => error?.text || error?.message || String(error || "Request failed");

const createFilters = () => ({
	dateRange: createDefaultLogDateRange(),
	id: "",
	uid: "",
	number: "",
	type: "",
	status: "",
});

export class SmsRoutingLogs extends React.Component {
	state = {
		loaded: false,
		items: [],
		error: "",
		filters: createFilters(),
		appliedFilters: createFilters(),
		pagination: { page: 1, limit: 25, total: 0, pages: 1 },
		details: { visible: false, loading: false, error: "", request: null, items: [] },
	};

	componentDidMount() {
		this.loadLogs();
	}

	request = (method, data) => this.props.request("get", method, data);

	requestData = (filters = this.state.appliedFilters, page = this.state.pagination.page, limit = this.state.pagination.limit) => {
		const data = {
			page,
			limit,
			dateFrom: filters.dateRange.startDate,
			dateTo: filters.dateRange.endDate,
		};
		for (const field of ["id", "uid", "number", "type", "status"]) {
			if (String(filters[field] ?? "").trim()) data[field] = String(filters[field]).trim();
		}
		return data;
	};

	loadLogs = (filters = this.state.appliedFilters, page = this.state.pagination.page, limit = this.state.pagination.limit) => {
		this.setState({ loaded: false, error: "" });
		this.request("logs", this.requestData(filters, page, limit))
			.then((response) => {
				const pagination = response?.pagination || {};
				this.setState({
					loaded: true,
					items: Array.isArray(response?.items) ? response.items : [],
					pagination: {
						page: Number(pagination.page) || page,
						limit: Number(pagination.limit) || limit,
						total: Number(pagination.total) || 0,
						pages: Number(pagination.pages) || 1,
					},
				});
			})
			.catch((error) => this.setState({ loaded: true, items: [], error: getErrorText(error) }));
	};

	setFilter = (field, value) => this.setState((state) => ({ filters: { ...state.filters, [field]: value } }));

	validateDateRange = (range) => {
		if (!range || range.type !== "absolute" || !range.startDate || !range.endDate) {
			return { valid: false, errorMessage: "Choose both start and end dates" };
		}
		if (range.startDate > range.endDate) return { valid: false, errorMessage: "Start date must not be after end date" };
		return { valid: true };
	};

	applyFilters = () => {
		const validation = this.validateDateRange(this.state.filters.dateRange);
		if (!validation.valid) return this.setState({ error: validation.errorMessage });
		const appliedFilters = { ...this.state.filters, dateRange: { ...this.state.filters.dateRange } };
		this.setState((state) => ({ appliedFilters, pagination: { ...state.pagination, page: 1 } }), () => this.loadLogs(appliedFilters, 1));
	};

	resetFilters = () => {
		const filters = createFilters();
		this.setState((state) => ({ filters, appliedFilters: filters, pagination: { ...state.pagination, page: 1 } }), () => this.loadLogs(filters, 1));
	};

	handleKeyDown = (event) => {
		const key = event?.detail?.key || event?.key;
		if (key !== "Enter" || event?.nativeEvent?.isComposing) return;
		event.preventDefault?.();
		this.applyFilters();
	};

	openDetails = (request) => {
		this.setState({ details: { visible: true, loading: true, error: "", request, items: [] } });
		this.request("logDetails", { id: request.id })
			.then((response) => this.setState({ details: { visible: true, loading: false, error: "", request, items: Array.isArray(response?.items) ? response.items : [] } }))
			.catch((error) => this.setState({ details: { visible: true, loading: false, error: getErrorText(error), request, items: [] } }));
	};

	closeDetails = () => this.setState({ details: { visible: false, loading: false, error: "", request: null, items: [] } });

	renderFilters = () => {
		const filters = this.state.filters;
		return (
			<div onKeyDown={this.handleKeyDown}>
				<SpaceBetween size="m">
					<Grid gridDefinition={[{ colspan: 4 }, { colspan: 2 }, { colspan: 2 }, { colspan: 4 }]}>
						<FormField label="Period">
							<DateRangePicker dateOnly value={filters.dateRange} relativeOptions={[]} i18nStrings={DATE_RANGE_I18N} isValidRange={this.validateDateRange} onChange={({ detail }) => this.setFilter("dateRange", detail.value)} />
						</FormField>
						<FormField label="SMS ID">
							<Input inputMode="numeric" value={filters.id} onChange={({ detail }) => this.setFilter("id", detail.value)} />
						</FormField>
						<FormField label="UID">
							<Input inputMode="numeric" value={filters.uid} onChange={({ detail }) => this.setFilter("uid", detail.value)} />
						</FormField>
						<FormField label="Phone number">
							<Input inputMode="tel" value={filters.number} placeholder="Example: +998 90 123 45 67" onChange={({ detail }) => this.setFilter("number", detail.value)} />
						</FormField>
					</Grid>
					<Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 6 }]}>
						<FormField label="OTP type">
							<Select selectedOption={TYPE_OPTIONS.find((option) => option.value === filters.type) || TYPE_OPTIONS[0]} options={TYPE_OPTIONS} onChange={({ detail }) => this.setFilter("type", detail.selectedOption.value)} />
						</FormField>
						<FormField label="SMS status">
							<Select selectedOption={STATUS_OPTIONS.find((option) => option.value === filters.status) || STATUS_OPTIONS[0]} options={STATUS_OPTIONS} onChange={({ detail }) => this.setFilter("status", detail.selectedOption.value)} />
						</FormField>
						<Box padding={{ top: "l" }}>
							<SpaceBetween direction="horizontal" size="xs">
								<Button variant="primary" iconName="search" loading={!this.state.loaded} onClick={this.applyFilters}>Search</Button>
								<Button disabled={!this.state.loaded} onClick={this.resetFilters}>Reset</Button>
							</SpaceBetween>
						</Box>
					</Grid>
				</SpaceBetween>
			</div>
		);
	};

	renderDetails = () => {
		const details = this.state.details;
		if (!details.visible) return null;
		return (
			<Modal visible size="large" header={`OTP routing details — SMS #${details.request?.id || ""}`} onDismiss={this.closeDetails} footer={<Box float="right"><Button onClick={this.closeDetails}>Close</Button></Box>}>
				<SpaceBetween size="m">
					<Alert type="info">Accepted means that the provider API accepted the request. It does not confirm delivery to the phone.</Alert>
					{details.error ? <Alert type="error" header="Could not load routing details">{details.error}</Alert> : null}
					<Table
						trackBy="id"
						loading={details.loading}
						items={details.items}
						empty={<Box textAlign="center">Routing data is unavailable. This may be a legacy SMS or its routing record was not created.</Box>}
						columnDefinitions={[
							{ id: "step", header: "Step", cell: (item) => item.step },
							{ id: "providerId", header: "Provider ID", cell: (item) => item.providerId ?? "—" },
							{ id: "providerDriver", header: "Provider driver", cell: (item) => item.providerDriver || "—" },
							{ id: "status", header: "Status", cell: (item) => <StatusIndicator type={routingStatusType(item.status)}>{routingStatusLabel(item.status)}</StatusIndicator> },
							{ id: "reason", header: "Reason", cell: (item) => item.reason || "—" },
							{ id: "providerRequestId", header: "Provider request ID", cell: (item) => item.providerRequestId || "—" },
							{ id: "fallbackAt", header: "Fallback time", cell: (item) => formatDateTime(item.fallbackAt) },
							{ id: "createdAt", header: "Created", cell: (item) => formatDateTime(item.createdAt) },
						]}
					/>
				</SpaceBetween>
			</Modal>
		);
	};

	render() {
		const { pagination } = this.state;
		return (
			<SpaceBetween size="m">
				{this.state.error ? <Alert type="error" dismissible onDismiss={() => this.setState({ error: "" })}>{this.state.error}</Alert> : null}
				<Table
					stickyHeader
					trackBy="id"
					loading={!this.state.loaded}
					items={this.state.items}
					filter={this.renderFilters()}
					header={<Header counter={`(${pagination.total})`} description="SMS requests for the selected period. Open an ID to inspect the OTP provider cascade.">SMS / OTP logs</Header>}
					empty={<Box textAlign="center">No SMS requests found for the selected filters.</Box>}
					columnDefinitions={[
						{ id: "id", header: "ID", cell: (item) => <Button variant="link" onClick={() => this.openDetails(item)}>{item.id}</Button> },
						{ id: "date", header: "Date", cell: (item) => formatDateTime(item.date) },
						{ id: "uid", header: "UID", cell: (item) => item.uid },
						{ id: "number", header: "Phone", cell: (item) => item.number },
						{ id: "type", header: "Type", cell: (item) => item.type || "—" },
						{ id: "status", header: "Status", cell: (item) => <StatusIndicator type={smsStatusType(item.status)}>{smsStatusLabel(item.status)}</StatusIndicator> },
						{ id: "gateway", header: "Gateway", cell: (item) => item.gateway || "—" },
						{ id: "retries", header: "Retries", cell: (item) => item.retries },
					]}
					pagination={<Pagination disabled={!this.state.loaded} currentPageIndex={pagination.page} pagesCount={pagination.pages} onChange={({ detail }) => this.setState((state) => ({ pagination: { ...state.pagination, page: detail.currentPageIndex } }), () => this.loadLogs(this.state.appliedFilters, detail.currentPageIndex))} />}
					preferences={<CollectionPreferences title="Preferences" confirmLabel="Confirm" cancelLabel="Cancel" preferences={{ pageSize: pagination.limit }} pageSizePreference={{ title: "SMS requests per page", options: PAGE_SIZE_OPTIONS }} onConfirm={({ detail }) => this.setState((state) => ({ pagination: { ...state.pagination, page: 1, limit: detail.pageSize } }), () => this.loadLogs(this.state.appliedFilters, 1, detail.pageSize))} />}
				/>
				{this.renderDetails()}
			</SpaceBetween>
		);
	}
}

export default SmsRoutingLogs;
